import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Sparkles,
  ChevronDown,
  Building2,
  Menu,
  X,
} from "lucide-react";
import logo from "../../images/logo.svg";
import serverUrl from "../../utils/utilsser";
import { useAuth } from "../../context/authContext";
import ThemeToggle from "../ui/ThemeToggle";
import { navItems } from "../../config/navItems";

// ---------------------------------------------------------------------------
// Infiheal brand tokens — same palette used across the dashboard pages.
// ---------------------------------------------------------------------------

const BRAND = {
  gradient: "linear-gradient(135deg, #ef5da8 0%, #8b7cf6 100%)",
  teal: "#34d9b8",
  pink: "#ef5da8",
  purple: "#8b7cf6",
};

// Fallback default branch to select on first load when nothing is stored yet.
const DEFAULT_CHILD_CODE = "KW";

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { code, setCode } = useAuth();
  const [childCodes, setChildCodes] = useState([]);
  const [codesOpen, setCodesOpen] = useState(false);

const activeItem = navItems.find((item) => {
  if (item.path === "/") {
    return location.pathname === "/";
  }
  return (
    location.pathname === item.path ||
    location.pathname.startsWith(`${item.path}/`)
  );
});
  const active = activeItem?.key ?? navItems[0]?.key;
  const homePath = navItems.find((item) => item.key === "home")?.path ?? "/";

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("email");
    localStorage.removeItem("code");
    localStorage.removeItem("org");
    window.location.reload();
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileOpen(false);
  };
  useEffect(() => {
    const parentCode = localStorage.getItem("code");
    if (!parentCode) return;

    if (parentCode == "AIIMS") {
      setCode(parentCode);
      return;
    }
    const loadChildCodes = async () => {
      try {
        const res = await fetch(
          `${serverUrl}api/v1/coupon/get-child-codes/${parentCode}`,
        );
        if (!res.ok) return;

        const data = await res.json();
        const uniqueChildren = Array.from(new Set(data ?? []));
        setChildCodes(uniqueChildren);

        // Decide what should be selected by default: whatever was already
        // chosen and stored (if it's still a valid child), otherwise KW,
        // otherwise just the first child in the list.
        const storedChild = localStorage.getItem("c-code");
        if (storedChild && uniqueChildren.includes(storedChild)) {
          setCode(storedChild);
        } else if (uniqueChildren.includes(DEFAULT_CHILD_CODE)) {
          setCode(DEFAULT_CHILD_CODE);
          localStorage.setItem("c-code", DEFAULT_CHILD_CODE);
        } else if (uniqueChildren.length > 0) {
          setCode(uniqueChildren[0]);
          localStorage.setItem("c-code", uniqueChildren[0]);
        }
      } catch (error) {
        console.log(error);
      }
    };

    loadChildCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleSelectCode = (item) => {
    if (item !== "NASHIK") {
      setCode(item);
      localStorage.setItem("c-code", item);
    } else {
      setCode(item);
      localStorage.removeItem("c-code");
      localStorage.setItem("code", item);
    }
    setCodesOpen(false);
    setMobileOpen(false);
    navigate(homePath);
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  return (
    <>
 
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        />
      )}

      <div
        className={`h-[100dvh] overflow-y-auto custom-scrollbar flex flex-col items-center
          w-[280px] xl:w-[320px] shrink-0 text-slate-900 dark:text-white py-6
          bg-white dark:bg-[#0B0C12] border-r border-black/[0.06] dark:border-white/[0.06]
          bg-[radial-gradient(circle_at_20%_0%,rgba(239,93,168,0.05),transparent_45%)]
          dark:bg-[radial-gradient(circle_at_20%_0%,rgba(139,124,246,0.07),transparent_45%)]
          fixed inset-y-0 left-0 z-50 transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:static lg:translate-x-0 lg:z-auto`}
      >
        {/* Close button — mobile/tablet only, sits inside the open panel. */}
        <button
          onClick={() => setMobileOpen(false)}
          aria-label="Close menu"
          className="lg:hidden self-end mr-4 mb-2 p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white/80 transition-colors"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <img src={logo} alt="logo" className="w-[130px]" />
        <span
          className="mt-2 flex items-center gap-1 rounded-full border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.03] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: BRAND.teal }}
        >
          <Sparkles size={11} strokeWidth={2.5} />
          Healo™ HR
        </span>

        <div className="w-full flex flex-col items-center gap-2 pt-8 px-5">
          {navItems
            .filter((item) => {
              if (code === "WAP" || code === "WAPTEST" ) {
                const isHome = item.key === "home";
                const isGrievance =
                  item.key === "grievances" ||
                  item.key === "grievance" ||
                  item.name?.toLowerCase().includes("grievance") ||
                  item.path?.toLowerCase().includes("grievance");

                return isHome || isGrievance;
              }
              if (code == "AIIMS") {
                const isHome = item.key === "home";
                const isAnlytics = item.key === "Analytics";
                const isMood = item.key === "mood";
                return isHome || isAnlytics || isMood;
              }
               // show all items for other codes
               return true;
            })
            .map((item) => {
              const isActive = active === item.key;

              return (
                <button
                  key={item.key}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full py-2.5 rounded-xl text-left px-4 text-sm font-medium transition-all duration-200
            ${
              isActive
                ? "text-white shadow-lg shadow-[#8C7CF0]/25"
                : "text-slate-500 dark:text-white/70 hover:bg-black/[0.05] dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-white"
            }`}
                  style={isActive ? { background: BRAND.gradient } : undefined}
                >
                  {item.name}
                </button>
              );
            })}
        </div>

        {/* Branch / child-code switcher — collapsible dropdown so it stays
            compact no matter how many child codes exist, and is available on
            every page since Sidebar is shared layout. */}
        {code && code == "AIIMS" ? (
          ""
        ) : (
          <div className="w-full px-5 pt-6">
            <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-white/40">
              <Building2 size={11} strokeWidth={2.5} />
              Branch
            </div>

            <button
              onClick={() => setCodesOpen((v) => !v)}
              className="w-full flex items-center justify-between rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.04] px-4 py-2.5 transition-colors duration-200 hover:bg-black/[0.05] dark:hover:bg-white/[0.07]"
            >
              <span className="truncate text-sm font-semibold text-slate-700 dark:text-white/85">
                {code}
              </span>
              <ChevronDown
                size={15}
                strokeWidth={2.5}
                className={`shrink-0 text-slate-400 dark:text-white/40 transition-transform duration-200 ${
                  codesOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <div
              className={`grid transition-all duration-300 ease-in-out ${
                codesOpen
                  ? "grid-rows-[1fr] opacity-100 mt-2"
                  : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="max-h-56 overflow-y-auto custom-scrollbar flex flex-col gap-1.5 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02] p-2">
                  {childCodes.length === 0 ? (
                    <span className="px-3 py-2 text-xs text-slate-400 dark:text-white/40">
                      No branches found
                    </span>
                  ) : (
                    childCodes.map((item) => (
                      <button
                        key={item}
                        onClick={() => handleSelectCode(item)}
                        className={`w-full text-xs py-2 rounded-lg text-left px-3 font-medium transition-all duration-200
                        ${
                          code === item
                            ? "bg-[#8C7CF0]/20 text-[#6D5AE0] dark:text-[#C9C0FF] border border-[#8C7CF0]/40"
                            : "text-slate-500 dark:text-white/60 border border-transparent hover:bg-black/[0.05] dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-white"
                        }`}
                      >
                        {item}
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* <div className="w-full px-5 pt-6">
          <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-white/40">
            <Building2 size={11} strokeWidth={2.5} />
            Branch
          </div>

          <button
            onClick={() => setCodesOpen((v) => !v)}
            className="w-full flex items-center justify-between rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.03] dark:bg-white/[0.04] px-4 py-2.5 transition-colors duration-200 hover:bg-black/[0.05] dark:hover:bg-white/[0.07]"
          >
            <span className="truncate text-sm font-semibold text-slate-700 dark:text-white/85">
              {code}
            </span>
            <ChevronDown
              size={15}
              strokeWidth={2.5}
              className={`shrink-0 text-slate-400 dark:text-white/40 transition-transform duration-200 ${
                codesOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          <div
            className={`grid transition-all duration-300 ease-in-out ${
              codesOpen
                ? "grid-rows-[1fr] opacity-100 mt-2"
                : "grid-rows-[0fr] opacity-0"
            }`}
          >
            <div className="overflow-hidden">
              <div className="max-h-56 overflow-y-auto custom-scrollbar flex flex-col gap-1.5 rounded-xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.02] p-2">
                {childCodes.length === 0 ? (
                  <span className="px-3 py-2 text-xs text-slate-400 dark:text-white/40">
                    No branches found
                  </span>
                ) : (
                  childCodes.map((item) => (
                    <button
                      key={item}
                      onClick={() => handleSelectCode(item)}
                      className={`w-full text-xs py-2 rounded-lg text-left px-3 font-medium transition-all duration-200
                        ${
                          code === item
                            ? "bg-[#8C7CF0]/20 text-[#6D5AE0] dark:text-[#C9C0FF] border border-[#8C7CF0]/40"
                            : "text-slate-500 dark:text-white/60 border border-transparent hover:bg-black/[0.05] dark:hover:bg-white/[0.06] hover:text-slate-900 dark:hover:text-white"
                        }`}
                    >
                      {item}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div> */}

        <div className="mt-auto flex flex-col items-center gap-4 pt-6">
          <ThemeToggle />
          <button
            onClick={handleLogout}
            title="Logout"
            className="p-3 rounded-full text-slate-400 dark:text-white/50 hover:text-[#FF5C7A] hover:bg-[#FF5C7A]/10 transition-colors duration-200"
          >
            <LogOut size={22} />
          </button>
          <p className="text-[10px] text-slate-300 dark:text-white/25">
            Made with <span style={{ color: BRAND.pink }}>💗</span> by Infiheal
          </p>
        </div>
      </div>
    </>
  );
};

var Sidebar_default = Sidebar;
export { Sidebar_default as default };
